import type { QuestionDto } from "@green0meter/api-client";
import React, { FC, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";

import AIAssistantDrawer from "./components/AIAssistantDrawer";
import DoubleMaterialityQuestionsTable from "./components/DoubleMaterialityQuestionsTable";
import DoubleMaterialityRangesDrawer, {
  DmRangesFormValue,
} from "./components/DoubleMaterialityRangesDrawer";
import QuestionEditor from "./QuestionEditor";

import { useGetSurveys, useUpdateSurvey } from "@api";
import { useUserStore } from "@store/user";

const DoubleMaterialityQuestions: FC = () => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useUserStore();
  const [searchParams] = useSearchParams();
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<QuestionDto | null>(null);
  const [aiDrawerOpen, setAiDrawerOpen] = useState(false);
  const [rangesDrawerOpen, setRangesDrawerOpen] = useState(false);

  const yearParam = searchParams.get("year");
  const currentYear = yearParam ? parseInt(yearParam, 10) : new Date().getFullYear();

  const { surveys } = useGetSurveys(user);
  const { updateSurveyAsync, isPendingUpdateSurvey, updateSurveyEnabled } =
    useUpdateSurvey(user);

  const activeSurvey = useMemo(
    () => surveys?.find((survey) => survey.year === currentYear) ?? null,
    [surveys, currentYear],
  );

  const handleClose = (): void => {
    setOpen(false);
    setSelected(null);
  };

  const handleOpenRanges = (): void => {
    if (!activeSurvey) {
      enqueueSnackbar(
        t(
          "doubleMateriality.ranges.noSurveyForYear",
          "Create a survey for this year before configuring ranges.",
        ),
        { variant: "warning" },
      );

      return;
    }

    setRangesDrawerOpen(true);
  };

  const handleSaveRanges = async (ranges: DmRangesFormValue): Promise<void> => {
    if (!activeSurvey) {
      enqueueSnackbar(
        t(
          "doubleMateriality.ranges.noSurveyForYear",
          "Create a survey for this year before configuring ranges.",
        ),
        { variant: "warning" },
      );

      return;
    }

    if (!updateSurveyEnabled) {
      enqueueSnackbar(
        t(
          "doubleMateriality.ranges.permissionError",
          "You do not have permission to update ranges.",
        ),
        { variant: "warning" },
      );

      return;
    }

    try {
      await updateSurveyAsync({ surveyId: activeSurvey.id, params: { ranges } });
      enqueueSnackbar(
        t("doubleMateriality.ranges.saveSuccess", "Ranges updated successfully."),
        { variant: "success" },
      );
      setRangesDrawerOpen(false);
    } catch (error) {
      enqueueSnackbar(
        t(
          "doubleMateriality.ranges.saveError",
          "We could not save your changes. Please try again.",
        ),
        { variant: "error" },
      );
    }
  };

  return (
    <>
      <DoubleMaterialityQuestionsTable
        onAddQuestion={() => setOpen(true)}
        onEditQuestion={(q) => {
          setSelected(q);
          setOpen(true);
        }}
        onOpenAIAssistant={() => setAiDrawerOpen(true)}
        onOpenRanges={handleOpenRanges}
      />
      <QuestionEditor open={open} onClose={handleClose} question={selected} />
      <AIAssistantDrawer
        open={aiDrawerOpen}
        onClose={() => setAiDrawerOpen(false)}
      />
      <DoubleMaterialityRangesDrawer
        open={rangesDrawerOpen}
        onClose={() => setRangesDrawerOpen(false)}
        initialRanges={(activeSurvey?.ranges as Record<string, unknown>) ?? null}
        onSave={handleSaveRanges}
        isSaving={isPendingUpdateSurvey}
        canEdit={updateSurveyEnabled}
      />
    </>
  );
};

export default DoubleMaterialityQuestions;
